import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { addMonths } from 'date-fns/addMonths'
import { addWeeks } from 'date-fns/addWeeks'
import { endOfMonth } from 'date-fns/endOfMonth'
import { endOfWeek } from 'date-fns/endOfWeek'
import { format } from 'date-fns/format'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfWeek } from 'date-fns/startOfWeek'
import { subMonths } from 'date-fns/subMonths'
import { subWeeks } from 'date-fns/subWeeks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { PusherEvent, FEATURE_FLAGS } from '@mondaygpt/constants'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { useAnalytics } from 'hooks'
import { useIsMobile } from 'hooks/use-mobile'
import { SCHEDULE_TOOL_ACTIVITY_EVENT } from 'modules/creative/assistant-chat-events'
import * as ErrorReporting from 'services/error-reporting/error-reporting'
import { useAppDispatch } from 'modules/core'
import { openTopUpModal } from 'modules/billing'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import { useCreativeStore } from 'modules/creative/store/creative-store'
import { useIdeaStore, type PostIdea } from 'modules/creative/store/idea-store'
import { PostModal } from 'modules/creative/components/PostModal'
import { useFeatureGate } from 'modules/feature-flags'
import type { EngagementPostDto } from 'openapi'
import { normalizePermalink, useEngagementStore } from '../store/engagement-store'
import { LivePostModal } from '../components/LivePostModal'
import { useEnsureBrandKitTimezone } from '../../common/hooks/use-ensure-brand-kit-timezone'
import { useScheduleTimezone } from '../../common/hooks/use-brand-timezone'
import { splitIsoInTz } from '../../common/utils/business-timezone'
import {
  consumeCalendarGenerationTrigger,
  markCreativeGenerationFinalized,
} from '../analytics/calendar-generation-tracking'
import { useCalendarStore } from '../store/calendar-store'
import { composerUrlForEntry } from '../composer-url'
import { CalendarHeader } from '../components/CalendarHeader'
import { ContentCalendar } from '../components/ContentCalendar'
import { CalendarPopoverCard } from '../components/CalendarPopoverCard'
import { PlanPitchBanner } from '../components/PlanPitchBanner'
import { MobileBottomSheet } from '../../creative/components/MobileBottomSheet'
import { MobilePageHeader } from '../../creative/components/MobilePageHeader'
import { CreatePostButton } from '../components/CreatePostButton'
import { ScheduleDialog } from '../components/ScheduleDialog'
import { KeyboardShortcutsOverlay } from '../components/KeyboardShortcutsOverlay'
import { useCalendarShortcuts } from '../hooks/useCalendarShortcuts'
import type { CalendarEntry, CalendarView } from '../types'

// Read the current visible calendar range straight from the store, so Pusher
// handlers don't close over a stale `from`/`to` when the user flipped
// week↔month or paged to another month between trigger and completion.
// Same shape as CalendarPopoverCard.getViewDateRange — intentionally
// duplicated (rather than imported) to avoid a circular import through the
// calendar components barrel.
function getCurrentViewRange(): { from: string; to: string } {
  const { currentDate, view } = useCalendarStore.getState()
  if (view === 'week') {
    return {
      from: format(startOfWeek(currentDate), 'yyyy-MM-dd'),
      to: format(endOfWeek(currentDate), 'yyyy-MM-dd'),
    }
  }
  return {
    from: format(startOfWeek(startOfMonth(currentDate)), 'yyyy-MM-dd'),
    to: format(endOfWeek(endOfMonth(currentDate)), 'yyyy-MM-dd'),
  }
}

export function CalendarPage() {
  // Lazy-capture the brand timezone on first visit if it hasn't been set yet.
  // Critical so calendar bucketing/display + future scheduling all anchor to
  // the same zone. Fires at most once per session and no-ops once captured.
  useEnsureBrandKitTimezone()
  // The zone the reschedule picker anchors its wall-clock options to. Used to
  // seed the picker with the dragged post's ORIGINAL time-of-day (see below).
  const scheduleTimezone = useScheduleTimezone()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const dispatch = useAppDispatch()

  // Native (non-Taka) live posts open the read-only LivePostModal; they're
  // live-fetched (gated) and merged onto the calendar. Taka posts use the
  // PostModal hub below (activeIdea).
  const [livePost, setLivePost] = useState<EngagementPostDto | null>(null)
  const engagementEnabled = useFeatureGate(FEATURE_FLAGS.analyticsSection)
  const loadEngagement = useEngagementStore(s => s.load)
  const getEngagement = useEngagementStore(s => s.getByPermalink)
  const engagementMap = useEngagementStore(s => s.byPermalink)
  useEffect(() => {
    if (engagementEnabled) loadEngagement()
  }, [engagementEnabled, loadEngagement])

  const entries = useCalendarStore(s => s.entries)
  const loading = useCalendarStore(s => s.loading)
  const currentDate = useCalendarStore(s => s.currentDate)
  const view = useCalendarStore(s => s.view)
  const fetchEntries = useCalendarStore(s => s.fetchEntries)
  const setCurrentDate = useCalendarStore(s => s.setCurrentDate)
  const setView = useCalendarStore(s => s.setView)

  const [draggingEntry, setDraggingEntry] = useState<CalendarEntry | null>(null)
  const [dragSourceWidth, setDragSourceWidth] = useState<number | null>(null)
  const [pendingReschedule, setPendingReschedule] = useState<{
    entry: CalendarEntry
    targetDate: string
  } | null>(null)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  // Ensure creatives are loaded — Pusher generation-complete handlers refetch
  // them, and PostModal reads per-platform creatives the cards open into.
  const creativesLoaded = useCreativeStore(s => s.loaded)
  const fetchCreatives = useCreativeStore(s => s.fetchCreatives)
  useEffect(() => {
    if (!creativesLoaded) fetchCreatives()
  }, [creativesLoaded, fetchCreatives])

  // Track page view
  useEffect(() => {
    track(AnalyticsEvents.Calendar.CALENDAR_VIEWED, { view })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Compute date range for the current view
  const { from, to } = useMemo(() => {
    if (view === 'week') {
      const weekStart = startOfWeek(currentDate)
      const weekEnd = endOfWeek(currentDate)
      return { from: format(weekStart, 'yyyy-MM-dd'), to: format(weekEnd, 'yyyy-MM-dd') }
    }
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const calStart = startOfWeek(monthStart)
    const calEnd = endOfWeek(monthEnd)
    return { from: format(calStart, 'yyyy-MM-dd'), to: format(calEnd, 'yyyy-MM-dd') }
  }, [currentDate, view])

  useEffect(() => {
    fetchEntries(from, to)
  }, [from, to, fetchEntries])

  // The assistant dock floats OVER this page; when the agent moves a post the
  // grid behind it must follow live, not on the next reload.
  useEffect(() => {
    const handleScheduleToolActivity = () => {
      void fetchEntries(from, to)
    }
    window.addEventListener(SCHEDULE_TOOL_ACTIVITY_EVENT, handleScheduleToolActivity)
    return () =>
      window.removeEventListener(SCHEDULE_TOOL_ACTIVITY_EVENT, handleScheduleToolActivity)
  }, [from, to, fetchEntries])

  // Merge live native IG/FB posts (from the engagement feed) into the calendar
  // as 'published' entries on their date, deduped by permalink against Taka's
  // own entries. No projectId/creativeId → click routes to LivePostModal.
  const mergedEntries = useMemo<CalendarEntry[]>(() => {
    const feed = Object.values(engagementMap)
    if (!engagementEnabled || feed.length === 0) return entries
    const existing = new Set(
      entries.filter(e => e.permalink).map(e => normalizePermalink(e.permalink))
    )
    const synthetic: CalendarEntry[] = []
    for (const p of feed) {
      if (!p.permalink || !p.timestamp) continue
      const date = format(new Date(p.timestamp), 'yyyy-MM-dd')
      if (date < from || date > to || existing.has(normalizePermalink(p.permalink))) continue
      synthetic.push({
        id: `eng-${p.platform}-${p.postId}`,
        date,
        type: 'published',
        channel: p.platform,
        platforms: [p.platform],
        thumbnailUrl: p.imageUrl,
        permalink: p.permalink,
        creativeName: p.caption?.slice(0, 80),
        publishedAt: p.timestamp,
      })
    }
    return synthetic.length ? [...entries, ...synthetic] : entries
  }, [entries, engagementMap, engagementEnabled, from, to])

  // Listen for autopilot generation complete → refetch calendar + creatives so
  // the freshly generated post (a real creative) shows up.
  // NOTE: intentionally does NOT close over `from`/`to` — the user may have
  // flipped views or paged between trigger and this callback (see M6).
  // Reading the current range via getCurrentViewRange() inside the handler
  // also lets this callback be stable across range changes, so the Pusher
  // subscription doesn't churn on every navigation.
  const handleAutopilotComplete = useCallback(
    (_data: { autopilotId: string }) => {
      const { from: rangeFrom, to: rangeTo } = getCurrentViewRange()
      fetchEntries(rangeFrom, rangeTo)
      useCalendarStore.getState().fetchUpcoming()
      fetchCreatives()
    },
    [fetchEntries, fetchCreatives]
  )
  useListenToPusherEvent(PusherEvent.AutopilotGenerationComplete, handleAutopilotComplete)

  // Background AI generation (e.g. "+ Add post" on the calendar) finishes →
  // refetch entries so the new card shows the generated thumbnail/content.
  const handleCreativeGenerationComplete = useCallback(
    (data: { creativeId: string; status: 'ready' | 'failed'; type?: string; error?: string }) => {
      // Always mark the creative as finalized so CalendarDayCell stops
      // drawing the snake-border "generating" overlay — regardless of
      // whether the backend actually produced an image. Without this, a
      // ready-without-image completion leaves the snake looping forever.
      markCreativeGenerationFinalized(data.creativeId)

      const consumed = consumeCalendarGenerationTrigger(data.creativeId)
      const isCalendarInitiated = consumed !== undefined

      // Only fire calendar-scoped analytics + user-facing toast for
      // generations we triggered from the calendar — the assistant chat,
      // autopilot, and other surfaces have their own handlers for the same
      // Pusher event and we don't want to double-report or double-toast.
      if (isCalendarInitiated) {
        track(AnalyticsEvents.Calendar.CALENDAR_POST_GENERATION_COMPLETED, {
          creative_id: data.creativeId,
          status: data.status,
          ...(consumed?.channel ? { channel: consumed.channel } : {}),
          ...(data.type ? { creative_type: data.type } : {}),
          ...(data.error ? { error: data.error } : {}),
          duration_ms: consumed.durationMs,
        })

        if (data.status === 'failed') {
          if (data.error === 'insufficient_credits') {
            // Eager push: the generation died for lack of credits — surface the
            // buy popup right away (not just a toast) so the user can recover.
            toast.error("You've run out of credits. Add credits to keep generating.")
            dispatch(openTopUpModal({ source: 'generation_failed' }))
          } else {
            toast.error("We couldn't generate your post. Open it to try again.")
          }
        }
      }

      // See M6 above: read the range at callback-time, not capture-time, so
      // flipping views mid-generation refetches the view the user is
      // actually looking at.
      const { from: rangeFrom, to: rangeTo } = getCurrentViewRange()
      fetchEntries(rangeFrom, rangeTo)
      fetchCreatives()
    },
    [fetchEntries, fetchCreatives, track, dispatch]
  )
  useListenToPusherEvent(PusherEvent.CreativeGenerationComplete, handleCreativeGenerationComplete)

  // Live publish results (success or failure) flip a card's chrome — the acid
  // ✓ badge, the destructive stripe/corner, the failure chip. weekly-queue
  // handles its own optimistic patch, but calendar entries come from the
  // calendar store and only refresh on refetch. Bind the SAME refetch as the
  // generation-complete handlers so a card's state updates without a page
  // reload (reviewer-suggested cheap liveness win).
  //
  // Debounced: an N-sibling publish burst fires N CreativePublishComplete
  // events in the same tick. Without this the calendar hits fetchEntries +
  // fetchUpcoming N times back-to-back for the same post; one trailing refetch
  // after the storm settles is enough.
  const publishRefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleCreativePublishComplete = useCallback(() => {
    if (publishRefetchTimer.current) clearTimeout(publishRefetchTimer.current)
    publishRefetchTimer.current = setTimeout(() => {
      publishRefetchTimer.current = null
      const { from: rangeFrom, to: rangeTo } = getCurrentViewRange()
      fetchEntries(rangeFrom, rangeTo)
      useCalendarStore.getState().fetchUpcoming()
    }, 500)
  }, [fetchEntries])
  useEffect(
    () => () => {
      if (publishRefetchTimer.current) clearTimeout(publishRefetchTimer.current)
    },
    []
  )
  useListenToPusherEvent(PusherEvent.CreativePublishComplete, handleCreativePublishComplete)

  // PostModal hub — clicking a card loads the post's project into a PostIdea and
  // opens the same schedule / approve / view-live modal Home + the cockpit use.
  // `openingId` drives a brief per-card loading veil during the loadIdea round-trip.
  const loadIdea = useIdeaStore(s => s.loadIdea)
  const [activeIdea, setActiveIdea] = useState<PostIdea | null>(null)
  const [openingId, setOpeningId] = useState<string | null>(null)

  // Whole-card click → open PostModal. Load the post's project into a PostIdea
  // (loadIdea hydrates the idea + creative stores PostModal reads from), then
  // surface the modal. Legacy entries with no projectId fall back to the
  // composer (they can't be represented as a PostIdea).
  const handleOpenPost = useCallback(
    async (entry: CalendarEntry) => {
      // Native live post (from the engagement feed) — no Taka creative behind
      // it. Open the read-only LivePostModal instead of falling to the composer.
      if (!entry.creativeId) {
        if (entry.type === 'published') {
          const eng = getEngagement(entry.permalink)
          if (eng) setLivePost(eng)
        }
        return
      }
      track(AnalyticsEvents.Calendar.CALENDAR_ENTRY_TAPPED, {
        entry_type: entry.type,
        channel: entry.channel,
      })
      if (!entry.projectId) {
        navigate(composerUrlForEntry(entry), {
          state: { source: 'calendar', entry_source: 'calendar_entry' },
        })
        return
      }
      setOpeningId(entry.creativeId)
      try {
        const idea = await loadIdea(entry.projectId)
        if (idea) setActiveIdea(idea)
        else toast.error('Couldn’t open this post. Please try again.')
      } catch (err) {
        ErrorReporting.captureException(err, {
          tags: { feature: 'calendar', step: 'open_post' },
          extra: { creativeId: entry.creativeId, projectId: entry.projectId },
        })
        toast.error('Couldn’t open this post. Please try again.')
      } finally {
        setOpeningId(null)
      }
    },
    [navigate, track, loadIdea, getEngagement]
  )

  const handleDateChange = useCallback(
    (date: Date) => {
      setCurrentDate(date)
    },
    [setCurrentDate]
  )

  const handleViewChange = useCallback(
    (newView: CalendarView) => {
      track(AnalyticsEvents.Calendar.CALENDAR_VIEW_CHANGED, {
        from_view: view,
        to_view: newView,
      })
      setView(newView)
    },
    [view, setView, track]
  )

  // ─── Keyboard shortcuts ─────────────────────────────────────────────────

  const [showShortcuts, setShowShortcuts] = useState(false)

  useCalendarShortcuts(
    useMemo(
      () => ({
        onToday: () => setCurrentDate(new Date()),
        onWeekView: () => handleViewChange('week'),
        onMonthView: () => handleViewChange('month'),
        onPrev: () =>
          setCurrentDate(view === 'month' ? subMonths(currentDate, 1) : subWeeks(currentDate, 1)),
        onNext: () =>
          setCurrentDate(view === 'month' ? addMonths(currentDate, 1) : addWeeks(currentDate, 1)),
        onEscape: () => {
          setShowShortcuts(false)
        },
        onShowHelp: () => setShowShortcuts(prev => !prev),
      }),
      [currentDate, view, setCurrentDate, handleViewChange]
    )
  )

  // ─── Drag and drop ───────────────────────────────────────────────────────

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const entry = event.active.data.current?.entry as CalendarEntry | undefined
    if (entry) setDraggingEntry(entry)
    const width = event.active.rect.current.initial?.width
    if (width) setDragSourceWidth(width)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setDraggingEntry(null)
    setDragSourceWidth(null)
    const { active, over } = event
    if (!over) return

    const entry = active.data.current?.entry as CalendarEntry | undefined
    const targetDate = over.data.current?.date as string | undefined
    if (!entry || !targetDate || entry.date === targetDate) return
    if (entry.type === 'published') return
    if (targetDate < format(new Date(), 'yyyy-MM-dd')) return

    setPendingReschedule({ entry, targetDate })
  }, [])

  const scheduleCreative = useCalendarStore(s => s.scheduleCreative)

  const handleRescheduleConfirm = useCallback(
    async (scheduledAt: string) => {
      if (!pendingReschedule?.entry.creativeId) return
      // A refused reschedule (platform content limits, already published) used
      // to reject un-caught and the card silently snapped back. Log it, then
      // RETHROW: this is a ScheduleDialog `onSchedule`, which reads a resolved
      // promise as success — returning here would show "Scheduled!" and close
      // over a move the server refused. Rejecting keeps the dialog open with
      // the reason inline.
      try {
        await scheduleCreative(pendingReschedule.entry.creativeId, scheduledAt)
      } catch (err) {
        console.error('Failed to reschedule creative:', err)
        throw err
      }
      track(AnalyticsEvents.Calendar.CALENDAR_ENTRY_TAPPED, {
        entry_type: 'rescheduled',
        channel: pendingReschedule.entry.channel,
      })
      const { from: rangeFrom, to: rangeTo } = { from, to }
      fetchEntries(rangeFrom, rangeTo)
    },
    [pendingReschedule, scheduleCreative, track, from, to, fetchEntries]
  )

  // ─── Teaching state ─────────────────────────────────────────────────────

  // The grid always renders (the old AutopilotEducation takeover is gone) —
  // a zero-entry visible range instead flips ContentCalendar into its
  // teaching state, where the next few upcoming day cells show "+ Add post"
  // at rest. `!loading` avoids a hint flash before the first fetch resolves.
  const calendarEmpty = !loading && mergedEntries.length === 0

  // ─── Loading overlay ──────────────────────────────────────────────────

  const loadingOverlay = loading && (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg-2/60">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-line-bright border-t-primary" />
    </div>
  )

  const calendarContent = (
    <ContentCalendar
      currentDate={currentDate}
      view={view}
      entries={mergedEntries}
      onOpenPost={handleOpenPost}
      openingId={openingId}
      draggingEntry={draggingEntry}
      calendarEmpty={calendarEmpty}
    />
  )

  // ─── Mobile: month-only with day bottom sheet ──────────────────────────
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  // Horizontal swipe pages months (monkey: the calendar ignored swipes and
  // only the 38x32 Prev/Next buttons paged). Vertical-dominant gestures pass
  // through to scrolling untouched.
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  const handleDayTap = useCallback((dateStr: string) => {
    setSelectedDate(dateStr)
  }, [])

  const selectedDateEntries = useMemo(
    () => (selectedDate ? mergedEntries.filter(e => e.date === selectedDate) : []),
    [mergedEntries, selectedDate]
  )

  // Post detail surfaces — shared by the mobile + desktop returns. Taka posts
  // open the editable PostModal; native live posts open the read-only modal.
  const postModals = (
    <>
      <PostModal
        idea={activeIdea}
        onClose={() => setActiveIdea(null)}
        openedFrom="calendar_entry"
        entrySource="calendar_entry"
      />
      <LivePostModal post={livePost} onClose={() => setLivePost(null)} />
    </>
  )

  if (isMobile) {
    return (
      <div className="flex h-full flex-col">
        <MobilePageHeader title="Calendar" subtitle="Schedule and manage your content" />
        <CalendarHeader
          currentDate={currentDate}
          view="month"
          onDateChange={handleDateChange}
          onViewChange={handleViewChange}
          hideViewToggle
        />

        {/* Weekly-plan pitch — visible only while the business has no plan. */}
        <PlanPitchBanner className="mx-4 mt-1 mb-3" />

        <div
          className="relative flex-1 overflow-hidden"
          onTouchStart={e => {
            touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
          }}
          onTouchEnd={e => {
            const start = touchStartRef.current
            touchStartRef.current = null
            if (!start) return
            const dx = e.changedTouches[0].clientX - start.x
            const dy = e.changedTouches[0].clientY - start.y
            if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 2) return
            setCurrentDate(dx < 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1))
          }}
        >
          {loadingOverlay}
          <ContentCalendar
            currentDate={currentDate}
            view="month"
            entries={mergedEntries}
            onOpenPost={handleOpenPost}
            openingId={openingId}
            onDayTap={handleDayTap}
            calendarEmpty={calendarEmpty}
          />
        </div>

        <MobileBottomSheet
          open={!!selectedDate}
          onClose={() => setSelectedDate(null)}
          title={
            selectedDate ? format(new Date(selectedDate + 'T00:00:00'), 'EEEE, MMM d') : undefined
          }
        >
          <div className="space-y-3 overflow-y-auto p-4">
            {selectedDateEntries.length === 0 ? (
              <p className="py-8 text-center text-sm text-paper-faint">Nothing scheduled</p>
            ) : (
              selectedDateEntries.map(entry => (
                <CalendarPopoverCard
                  key={entry.id}
                  entry={entry}
                  onClose={() => setSelectedDate(null)}
                  onOpenPost={handleOpenPost}
                  opening={openingId === entry.creativeId}
                />
              ))
            )}
            {selectedDate && selectedDate >= format(new Date(), 'yyyy-MM-dd') && (
              <CreatePostButton
                date={selectedDate}
                variant="full"
                onCreated={() => setSelectedDate(null)}
              />
            )}
          </div>
        </MobileBottomSheet>
        {postModals}
      </div>
    )
  }

  // ─── Desktop ──────────────────────────────────────────────────────────────

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* Allow AppLayout's overflow-y-auto to scroll the page.
          Title scrolls away; CalendarHeader sticks to the top. */}
      <div className="flex flex-col">
        {/* Title + plan pitch — scrolls away */}
        <div className="mx-auto w-full max-w-7xl px-6 pt-6 pb-4">
          <Heading level="h2">Calendar</Heading>
          <Text as="p" variant="body-sm" tone="paper-faint" className="mt-1">
            Schedule and manage your content
          </Text>
          {/* Weekly-plan pitch — visible only while the business has no plan. */}
          <PlanPitchBanner className="mt-4" />
        </div>

        {/* CalendarHeader — sticky at top of viewport once title scrolls away */}
        <div className="sticky top-0 z-30 bg-bg-2">
          <div className="mx-auto w-full max-w-7xl px-6">
            <CalendarHeader
              currentDate={currentDate}
              view={view}
              onDateChange={handleDateChange}
              onViewChange={handleViewChange}
              onShowShortcuts={() => setShowShortcuts(true)}
            />
          </div>
        </div>

        {/* Calendar grid */}
        <div className="mx-auto flex w-full max-w-7xl flex-1 gap-0 px-6 pb-6">
          <div
            className="flex flex-1 flex-col rounded-b-lg border-x border-b"
            style={{ minHeight: 'calc(100svh - 120px)' }}
          >
            <div className="relative flex-1">
              {loadingOverlay}
              {calendarContent}
            </div>
          </div>
        </div>
      </div>

      {/* Reschedule time picker after drag-drop */}
      <ScheduleDialog
        open={!!pendingReschedule}
        onOpenChange={open => {
          if (!open) setPendingReschedule(null)
        }}
        onSchedule={handleRescheduleConfirm}
        initialDate={pendingReschedule?.targetDate}
        initialTime={
          // Preserve the post's ORIGINAL time-of-day across a drag-move. The old
          // `toISOString().slice(11,16)` read the time in UTC, but the picker's
          // options are wall-clock in the brand zone — so a mismatch snapped the
          // pick to the earliest slot (07:00), silently changing the time. Split
          // the instant in the SAME zone the picker uses.
          pendingReschedule?.entry.scheduledAt
            ? splitIsoInTz(pendingReschedule.entry.scheduledAt, scheduleTimezone).time
            : undefined
        }
      />

      {/* Drag overlay — static (pointer-events-none); onOpenPost is a no-op here. */}
      <DragOverlay dropAnimation={null}>
        {draggingEntry && (
          <div className="pointer-events-none" style={{ width: dragSourceWidth ?? 260 }}>
            <CalendarPopoverCard entry={draggingEntry} onClose={() => {}} />
          </div>
        )}
      </DragOverlay>

      <KeyboardShortcutsOverlay open={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {postModals}
    </DndContext>
  )
}
